"use client";

import { useEffect, useState, useRef } from "react";
import { Search, ChevronDown, TrendingUp, Plus } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";

const Navbar = ({ menuItems }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const [showDesktopSearch, setShowDesktopSearch] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isFixed, setIsFixed] = useState(false);

  const router = useRouter();
  const pathname = usePathname();
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDesktopSearch(false);
      }
    };

    if (showDesktopSearch) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDesktopSearch]);

  // Social Media Icons
  const socialIcons = [
    {
      Icon: () => (
        <svg
          stroke="currentColor"
          fill="currentColor"
          strokeWidth="0"
          version="1.2"
          baseProfile="tiny"
          viewBox="0 0 24 24"
          height="1.4em"
          width="1.4em"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M13 10h3v3h-3v7h-3v-7h-3v-3h3v-1.255c0-1.189.374-2.691 1.118-3.512.744-.823 1.673-1.233 2.786-1.233h2.096v3h-2.1c-.498 0-.9.402-.9.899v2.101z"></path>
        </svg>
      ),
      label: "Facebook",
      path: "https://www.facebook.com/RashtraBharatdigital/",
    },
    {
      Icon: () => (
        <svg
          stroke="currentColor"
          fill="currentColor"
          strokeWidth="0"
          viewBox="0 0 24 24"
          height="1.1em"
          width="1.1em"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M17.6874 3.0625L12.6907 8.77425L8.37045 3.0625H2.11328L9.58961 12.8387L2.50378 20.9375H5.53795L11.0068 14.6886L15.7863 20.9375H21.8885L14.095 10.6342L20.7198 3.0625H17.6874ZM16.6232 19.1225L5.65436 4.78217H7.45745L18.3034 19.1225H16.6232Z"></path>
        </svg>
      ),
      label: "X",
      path: "https://x.com/RBharatdigital",
    },
    {
      Icon: () => (
        <svg
          stroke="currentColor"
          fill="currentColor"
          strokeWidth="0"
          viewBox="0 0 16 16"
          height="1em"
          width="1em"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.9 3.9 0 0 0-1.417.923A3.9 3.9 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.9 3.9 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.9 3.9 0 0 0-.923-1.417A3.9 3.9 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599s.453.546.598.92c.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.5 2.5 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.5 2.5 0 0 1-.92-.598 2.5 2.5 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233s.008-2.388.046-3.231c.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92s.546-.453.92-.598c.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92m-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217m0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334"></path>
        </svg>
      ),
      label: "Instagram",
      path: "https://www.instagram.com/rashtrabharatdigital/",
    },
    {
      Icon: () => (
        <svg
          stroke="currentColor"
          fill="currentColor"
          strokeWidth="0"
          viewBox="0 0 256 256"
          height="1.3em"
          width="1.3em"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M188.84,120.46a68.14,68.14,0,0,0-10-6.23c-3.72-21.68-16.41-37.41-35.52-43.2C121.94,64.55,97.29,72.42,86,89.34a12,12,0,0,0,20,13.32c5.47-8.2,19.11-12.08,30.41-8.66a24.72,24.72,0,0,1,14.88,12.24,86.73,86.73,0,0,0-8.86-.45C108.56,105.79,84,125.22,84,152c0,22.9,17.54,39.52,41.71,39.52a52,52,0,0,0,37.23-16c6-6.23,12.88-16.46,15.72-32.07,6.2,6.42,9.34,14.67,9.34,24.59,0,17.74-19.07,44-60,44-45.76,0-68-27.48-68-84s22.24-84,68-84c31.08,0,51,12.42,60.8,38a12,12,0,0,0,22.4-8.62C197.77,38.44,169,20,128,20,68.67,20,36,58.35,36,128s32.67,108,92,108c31.36,0,51.08-12.05,62.11-22.15C203.81,201.28,212,184.14,212,168,212,148.36,204,131.92,188.84,120.46Zm-43.2,38.39a27.9,27.9,0,0,1-19.93,8.67c-8.17,0-17.71-4.06-17.71-15.52,0-15.26,17.84-22.21,34.41-22.21a60.23,60.23,0,0,1,13.51,1.52C155.36,142.93,151.84,152.41,145.64,158.85Z"></path>
        </svg>
      ),
      label: "Threads",
      path: "https://www.threads.com/@rashtrabharatdigital",
    },
    {
      Icon: () => (
        <svg
          stroke="currentColor"
          fill="currentColor"
          strokeWidth="0"
          viewBox="0 0 448 512"
          height="1.2em"
          width="1.2em"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z"></path>
        </svg>
      ),
      label: "LinkedIn",
      path: "https://www.linkedin.com/company/rashtrabharatdigital/",
    },
    {
      Icon: () => (
        <svg
          height="1.2em"
          width="1.2em"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      ),
      label: "YouTube",
      path: "https://www.youtube.com/@RashtraBharatdigital",
    },
    {
      Icon: () => (
        <svg
          height="1.2em"
          width="1.2em"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
        </svg>
      ),
      label: "Email",
      path: "mailto:info@rashtrabharat.com",
    },
  ];

  //
  const normalizePath = (path) => path.replace(/\/$/, "");

  const handleSearch = () => {
    if (!searchText.trim()) return;

    router.push(`/search?s=${encodeURIComponent(searchText)}`);
    setShowDesktopSearch(false); // optional: close search box
  };

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsFixed(window.scrollY > 200);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const design = (
    <>
      {/* Desktop Navigation */}
      <header className="hidden lg:block bg-white  border-gray-200 mb-5">
        {/* Top Search Bar */}
        <div className="container ">
          <div className="flex items-center gap-4 py-2">
            <div className="flex-1 ">
              <div
                ref={searchRef}
                className={`relative ${
                  showDesktopSearch
                    ? "animate__animated animate__fadeIn block"
                    : "animate__animated animate__fadeOutUp hidden "
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <input
                  type="search"
                  placeholder="Type & hit enter"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  className="w-full px-4 py-3 pr-10 bg-[#EAEAEA] border text-sm"
                />

                <button className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-900">
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Logo and Social Icons */}
        <div className="container  border-gray-200">
          <div className="container mx-auto  py-1">
            <div className="flex items-center justify-between">
              <button
                className="text-black"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDesktopSearch(true);
                }}
              >
                <Search className="w-4 h-4" />
              </button>

              <Link href="/" className="text-center ml-40">
                <Image
                  src="https://cdn.rashtrabharat.com/media/2026/07/07162224/Rashtra-Bharat-Logo.png"
                  alt="Rashtra Bharat Logo"
                  width={280}
                  height={67}
                  unoptimized
                  priority
                />
              </Link>

              <div className="flex items-center gap-2">
                {socialIcons.map((social, index) => (
                  <Link
                    key={index}
                    href={social.path}
                    target="_blank"
                    className="text-gray-700 hover:text-gray-900 transition-colors"
                    aria-label={social.label}
                  >
                    <social.Icon />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Navigation Menu */}
        <nav
          className={`bg-white border-t border-b py-1 transition-all duration-300
    ${isFixed ? "fixed top-[0px] left-0 right-0 z-20" : "mt-4 relative"}`}
        >
          <div className="container mx-auto">
            <ul className="flex flex-wrap items-center justify-between">
              {menuItems.map((item, index) => {
                const isLast = index === menuItems.length - 1;
                const hasChildren = item.children && item.children.length > 0;

                // 👉 LAST ITEM CUSTOM
                if (isLast) {
                  return (
                    <li key={item.id}>
                      <Link
                        href={item.url}
                        className="flex items-center gap-1 text-gray-800 hover:text-gray-900 text-xl transition-colors font-extrabold"
                      >
                        <TrendingUp className="w-4 h-4" />
                        वेब स्टोरी
                      </Link>
                    </li>
                  );
                }

                // 👉 NORMAL ITEMS
                return (
                  <li
                    key={item.id}
                    className="relative group"
                    onMouseEnter={
                      hasChildren ? () => setIsStateDropdownOpen(true) : null
                    }
                    onMouseLeave={
                      hasChildren ? () => setIsStateDropdownOpen(false) : null
                    }
                  >
                    <Link href={item.url} className="block relative">
                      <div
                        className={`relative flex items-center gap-1 text-gray-800 font-medium text-xl transition-colors -mt-1.5 pt-1

      after:content-[''] after:absolute after:left-[0%] after:top-0
      after:w-[100%] after:h-[2px] after:bg-current
      after:scale-x-0 after:origin-top after:transition-transform after:duration-150

      group-hover:after:scale-x-100

      ${
        normalizePath(pathname) === normalizePath(item.url)
          ? "after:scale-x-100"
          : ""
      }`}
                      >
                        <span
                          dangerouslySetInnerHTML={{ __html: item.title }}
                        />
                        {item.title === "राज्य" && <Plus className="w-3 h-3" />}
                      </div>
                    </Link>

                    {hasChildren && isStateDropdownOpen && (
                      <div className="absolute  left-0 top-[27px] bg-white shadow-lg border border-gray-200 min-w-[200px] z-50">
                        <ul>
                          {item.children.map((child) => (
                            <li
                              key={child.id}
                              className="border-b border-gray-300"
                            >
                              <Link
                                href={child.url}
                                className="block px-4 py-1 text-gray-800 text-[14px] hover:bg-gray-50 hover:text-gray-900"
                              >
                                {child.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
      </header>

      {/* Mobile/Tablet Navigation */}
      <div className="lg:hidden fixed top-0 w-full z-50 mb-10">
        {/* Mobile Header */}
        <header className="bg-white  border-gray-200 py-1 px-1">
          <div className="flex items-center justify-between h-[50px]">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="text-gray-700"
              aria-label="Open menu"
            >
              <Image
                src="https://cdn.rashtrabharat.com/media/2026/07/07162426/Menu-Icon.webp"
                alt="RashtraBharat, Latest Hindi News"
                width={32}
                height={32}
                className="icon-menu"
                sizes="(max-width: 64px) 100vw, 64px"
              />
            </button>

            <Link
              href="/"
              className="absolute left-1/2 transform -translate-x-1/2"
            >
              <Image
                src="https://cdn.rashtrabharat.com/media/2026/07/07162224/Rashtra-Bharat-Logo.png"
                alt="Rashtra Bharat Logo"
                width={210}
                height={50}
                unoptimized
                priority
              />
            </Link>

            <button className="text-gray-700"></button>
          </div>
        </header>

        {/* Mobile Sidebar Menu */}
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300 ${
            isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            className={`fixed left-0 top-0 bottom-0 w-80 bg-white shadow-xl transform transition-transform duration-300 ${
              isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Sidebar Header */}

            {/* Logo and Search */}
            <div className="px-4  border-gray-200">
              <div className="flex items-center justify-between my-4 mb-5">
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-black"
                  aria-label="Close menu"
                >
                  <svg
                    stroke="currentColor"
                    fill="none"
                    strokeWidth="0"
                    viewBox="0 0 24 24"
                    height="1.8em"
                    width="1.8em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.3394 9.32245C16.7434 8.94589 16.7657 8.31312 16.3891 7.90911C16.0126 7.50509 15.3798 7.48283 14.9758 7.85938L12.0497 10.5866L9.32245 7.66048C8.94589 7.25647 8.31312 7.23421 7.90911 7.61076C7.50509 7.98731 7.48283 8.62008 7.85938 9.0241L10.5866 11.9502L7.66048 14.6775C7.25647 15.054 7.23421 15.6868 7.61076 16.0908C7.98731 16.4948 8.62008 16.5171 9.0241 16.1405L11.9502 13.4133L14.6775 16.3394C15.054 16.7434 15.6868 16.7657 16.0908 16.3891C16.4948 16.0126 16.5171 15.3798 16.1405 14.9758L13.4133 12.0497L16.3394 9.32245Z"
                      fill="currentColor"
                    ></path>
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12ZM12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21Z"
                      fill="currentColor"
                    ></path>
                  </svg>
                </button>
                {/* <div className="text-center">
                  <Image
                    src="/Rashtra-Bharat-Logo-mobile.png"
                    alt="Rashtra Bharat Logo"
                    width={120}
                    height={50}
                    unoptimized
                  />
                </div> */}
              </div>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Type & hit enter"
                  className="w-full px-4 py-3.5 pr-10 bg-gray-100    text-sm focus:outline-none "
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />
                <button className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600">
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Navigation Items */}
            <nav className="flex-1 overflow-y-auto py-4">
              <ul className="space-y-1">
                {menuItems.map((item, index) => {
                  const isLast = index === menuItems.length - 1;
                  const hasChildren = item.children && item.children.length > 0;

                  // 👉 LAST ITEM CUSTOM
                  if (isLast) {
                    return (
                      <li key={item.id}>
                        <Link
                          href={item.url}
                          className="flex items-center gap-2 px-4 py-1.5 text-gray-900 font-extrabold hover:text-blue-300 transition-colors"
                        >
                          <TrendingUp className="w-4 h-4" />
                          वेब स्टोरी
                        </Link>
                      </li>
                    );
                  }

                  // 👉 NORMAL ITEMS
                  return (
                    <li key={item.id}>
                      {hasChildren ? (
                        <>
                          <button
                            type="button"
                            className="w-full px-4 py-1.5 flex items-center justify-between text-gray-800 hover:text-blue-300 transition-colors"
                            onClick={() =>
                              setIsStateDropdownOpen((prev) => !prev)
                            }
                          >
                            <span
                              className="font-medium"
                              dangerouslySetInnerHTML={{ __html: item.title }}
                            />
                            <ChevronDown
                              className={`w-4 h-4 transition-transform ${
                                isStateDropdownOpen ? "rotate-180" : ""
                              }`}
                            />
                          </button>

                          {isStateDropdownOpen && (
                            <ul className="pl-6 space-y-2  py-2">
                              {item.children.map((child) => (
                                <li key={child.id}>
                                  <Link
                                    href={child.url}
                                    className="block text-gray-700 hover:text-blue-300"
                                  >
                                    {child.title}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      ) : (
                        <Link
                          href={item.url}
                          className="block px-4 py-1.5 text-gray-800 hover:text-blue-300 font-normal transition-colors"
                        >
                          <span
                            dangerouslySetInnerHTML={{ __html: item.title }}
                          />
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Social Icons at Bottom */}
            <div className="border-t border-gray-200 p-4">
              <div className="flex items-center justify-center gap-4">
                {socialIcons.map((social, index) => (
                  <Link
                    key={index}
                    href={social.path}
                    target="_blank"
                    className="text-gray-700 hover:text-gray-900 transition-colors"
                    aria-label={social.label}
                  >
                    <social.Icon />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
  return design;
};

export default Navbar;
